import { useRouterState } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { OnboardingProvider } from "@/features/home/onboarding/onboarding.context";
import { CheckInLauncherProvider } from "@/features/home/onboarding/use-check-in-launcher";
import { LoginRoute } from "@/features/identity/routes/login.route";
import { useAuth } from "@/shared/auth/auth.context";
import { LogoMark, LogoWordmark } from "@/shared/components/brand/logo-mark";
import { CommandMenu } from "@/shared/components/command-menu/command-menu";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import { DemoWorkspaceBanner } from "@/shared/components/layout/demo-workspace-banner";
import { PageBreadcrumbProvider } from "@/shared/components/layout/page-breadcrumb";
import { isPayrollAdminPath } from "@/shared/components/layout/payroll-sidebar-model";
import { Sidebar } from "@/shared/components/layout/sidebar";
import { Topbar } from "@/shared/components/layout/topbar";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import {
  SidebarCollapsedProvider,
  useSidebarCollapsed,
} from "@/shared/hooks/use-sidebar-collapsed";

const COLLAPSED_WIDTH = "68px";

interface Props {
  children: ReactNode;
  copilotPanel?: ReactNode;
  copilotTrigger?: ReactNode;
}

export function AppShell(props: Props) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { workspace } = useActiveWorkspace();
  const scope = isPayrollAdminPath(pathname, workspace.slug) ? "payroll" : "workspace";
  return (
    <SidebarCollapsedProvider scope={scope}>
      <AppShellContent {...props} />
    </SidebarCollapsedProvider>
  );
}

function AppShellContent({ children, copilotPanel, copilotTrigger }: Props) {
  const { collapsed } = useSidebarCollapsed();
  const copilot = useCopilotLayout();
  const auth = useAuth();
  const { isSwitching } = useActiveWorkspace();

  // Initial /auth/me fetch in flight, render nothing rather than flash the
  // full shell or the login form. <500ms in normal conditions.
  if (auth.isLoading) {
    return <div className="min-h-svh bg-background" aria-hidden="true" />;
  }

  // Anonymous: show ONLY the login form, no chrome. The URL stays as-is so
  // post-login the router re-resolves to the intended destination.
  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <LoginRoute />
      </div>
    );
  }

  return (
    <PageBreadcrumbProvider>
      <CheckInLauncherProvider>
        <OnboardingProvider>
          <div
            style={
              {
                "--side-w": collapsed ? COLLAPSED_WIDTH : "var(--nav-w)",
                "--copilot-width": `${copilot.width}px`,
              } as CSSProperties
            }
            className="grid min-h-svh grid-cols-1 md:grid-cols-[var(--side-w)_minmax(0,1fr)]"
          >
            {/* Full-page Copilot covers the app, so keep focus and screen readers out of it. */}
            <div className="contents" inert={copilot.isExpanded}>
              <Sidebar />
            </div>
            <div className="min-w-0 bg-background" inert={copilot.isExpanded}>
              <div className="sticky top-0 z-20">
                <DemoWorkspaceBanner />
                <Topbar copilotTrigger={copilotTrigger} />
              </div>
              <main id="main" className="min-w-0">
                {children}
              </main>
            </div>
            {/* Fixed-position overlay, so it takes no grid cell and the page keeps its width. */}
            {copilotPanel}
          </div>
          {isSwitching ? <WorkspaceSwitchOverlay /> : null}
          <CommandMenu />
        </OnboardingProvider>
      </CheckInLauncherProvider>
    </PageBreadcrumbProvider>
  );
}

function WorkspaceSwitchOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/88 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card px-6 py-5 shadow-lg">
        <div className="relative flex items-center justify-center">
          <div className="size-12 animate-spin rounded-full border-2 border-border border-t-primary" />
          <LogoMark className="absolute size-6" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <LogoWordmark />
          <p className="text-sm text-text-secondary">Switching workspace...</p>
        </div>
      </div>
    </div>
  );
}
